import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppService } from './app.service';

export interface NutritionUnit {
  abr: string;
  abrMed: string;
  alternatives?: {
    unit: string;
    factor: number;
  }[];
}

@Injectable({
  providedIn: 'root'
})
export class NutritionService {

  private _units = {
    bar: {
      abr: "bar",
      abrMed: "bar",
    },
    cup: {
      abr: "c",
      abrMed: "cup",
      alternatives: [
        {
          unit: "ounce",
          factor: 8
        },
        {
          unit: "milliliter",
          factor: 236
        },
        {
          unit: "liter",
          factor: 0.23
        },
        {
          unit: "pint",
          factor: 0.5
        },
      ]
    },
    gram: {
      abr: "g",
      abrMed: "grams",
      alternatives: [
        {
          unit: "ounce",
          factor: 0.035
        },
        {
          unit: "teaspoon",
          factor: 0.25
        },
        {
          unit: "tablespoon",
          factor: 0.06666
        },
        {
          unit: "cup",
          factor: 0.005
        },
        {
          unit: "pound",
          factor: 0.0022
        },
        {
          unit: "kg",
          factor: 0.001
        }
      ],
    },
    kilogram: {
      abr: "kg",
      abrMed: "kg",
      alternatives: [
        {
          unit: "gram",
          factor: 1000
        },
        {
          unit: "ounce",
          factor: 35
        },
        {
          unit: "pound",
          factor: 2.2
        }
      ]
    },
    liter: {
      abr: "l",
      abrMed: "liter",
      alternatives: [
        {
          unit: "milliliter",
          factor: 1000
        },
        {
          unit: "cup",
          factor: 4.22
        },
        {
          unit: "pint",
          factor: 2.11
        },
      ]
    },
    milliliter: {
      abr: "ml",
      abrMed: "ml",
      alternatives: [
        {
          unit: "tablespoon",
          factor: 0.067
        },
        {
          unit: "teaspoon",
          factor: 0.2
        },
        {
          unit: "cup",
          factor: 0.004
        },
        {
          unit: "liter",
          factor: 0.001
        },
      ]
    },
    ounce: {
      abr: "oz",
      abrMed: "oz",
      alternatives: [
        {
          unit: "gram",
          factor: 28
        },
        {
          unit: "teaspoon",
          factor: 6
        },
        {
          unit: "tablespoon",
          factor: 2
        },
        {
          unit: "cup",
          factor: 0.125
        },
        {
          unit: "pound",
          factor: 0.06
        },
        {
          unit: "kilogram",
          factor: 0.03
        },
      ]
    },
    pint: {
      abr: "pt",
      abrMed: "pt",
      alternatives: [
        {
          unit: "milliliter",
          factor: 568
        },
        {
          unit: "liter",
          factor: 0.56
        },
        {
          unit: "cup",
          factor: 2.4
        },
      ]
    },
    pound: {
      abr: "p",
      abrMed: "lb",
      alternatives: [
        {
          unit: "gram",
          factor: 454
        },
        {
          unit: "ounce",
          factor: 16
        },
        {
          unit: "cup",
          factor: 3.05
        },
        {
          unit: "kilogram",
          factor: 0.454
        },
      ]
    },
    scoop: {
      abr: "sc",
      abrMed: "scoop"
    },
    slice: {
      abr: "sl",
      abrMed: "slice"
    },
    tablespoon: {
      abr: "tbl",
      abrMed: "tbsp",
      alternatives: [
        {
          unit: "gram",
          factor: 15
        },
        {
          unit: "teaspoon",
          factor: 3
        },
        {
          unit: "ounce",
          factor: 0.5
        },
        {
          unit: "cup",
          factor: 0.06
        },
      ]
    },
    teaspoon: {
      abr: "tsp",
      abrMed: "tsp",
      alternatives: [
        {
          unit: "gram",
          factor: 4.2
        },
        {
          unit: "tablespoon",
          factor: 0.33
        },  
        {
          unit: "ounce",
          factor: 0.167
        },
        {
          unit: "cup",
          factor: 0.02
        },
      ],
    },
    unit: {
      abr: "u",
      abrMed: "unit"
    },
    bottle: {
      abr: "btl",
      abrMed: "bottle"
    }
  }

  constructor(
    private appService: AppService,
    private http: HttpClient,
  ) {}

  get units() {
    return this._units
  }

  public getUnit(unit: string): NutritionUnit {
    return this._units[unit]
  }

  public getAbbreviation(unit, type: "short" | "medium" = "short") {
    if(this._units.hasOwnProperty(unit)) {
      return type ==="short"? this._units[unit].abr : this._units[unit].abrMed
    } else {
      return unit
    }
  }

  lookupFoodByUPC(upc: string) {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/nutrition/lookup/${upc}`)
      .toPromise()
      .then(res => { resolve(<any>res) })
      .catch(err => { reject(err) })
    });
  }
}